import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Select,
  Segmented,
  DatePicker,
  Tag,
  Image,
  Space,
  Empty,
  Typography,
} from "antd";
import { PictureOutlined } from "@ant-design/icons";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable";
import { listCameras, listPictures } from "../network/api";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";
import { TRAFFIC } from "../theme/themeConfig";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PictureViewer = () => {
  const { id } = useParams();

  const [cameras, setCameras] = useState([]);
  const [deviceId, setDeviceId] = useState(id || null);
  const [qr, setQr] = useState("all"); // "all" | "qr"
  const [range, setRange] = useState(null); // [dayjs, dayjs] | null

  // Populate the device dropdown.
  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const res = await listCameras({ page: 1, pageSize: 200 });
        setCameras(res?.data || []);
      } catch (e) {
        notify.error(serverError(e, "Failed to load devices"));
      }
    };
    fetchCameras();
  }, []);

  // Keep the route param in sync if it changes.
  useEffect(() => {
    if (id) setDeviceId(id);
  }, [id]);

  const deviceOptions = useMemo(
    () =>
      cameras.map((cam) => ({
        value: cam.deviceId,
        label: cam.deviceTitle ? `${cam.deviceId} (${cam.deviceTitle})` : cam.deviceId,
      })),
    [cameras]
  );

  // Server-side filters passed straight to listPictures.
  const filters = useMemo(
    () => ({
      qr: qr === "qr" ? true : undefined,
      from: range?.[0] ? range[0].startOf("day").toISOString() : undefined,
      to: range?.[1] ? range[1].endOf("day").toISOString() : undefined,
    }),
    [qr, range]
  );

  const columns = [
    {
      title: "Thumbnail",
      dataIndex: "absolutePath",
      key: "thumbnail",
      width: 120,
      render: (absolutePath) =>
        absolutePath ? (
          <Image
            src={absolutePath}
            alt="capture"
            width={80}
            height={56}
            style={{ objectFit: "cover", borderRadius: 6 }}
            loading="lazy"
            preview={{ src: absolutePath }}
          />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Captured",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (createdAt) =>
        createdAt ? (
          <Space direction="vertical" size={0}>
            <Text>{relativeTime(createdAt)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {new Date(createdAt).toLocaleString()}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "QR",
      dataIndex: "qr",
      key: "qr",
      width: 110,
      render: (value) =>
        value ? (
          <Tag color="green">QR</Tag>
        ) : (
          <Tag color="default">No QR</Tag>
        ),
    },
    {
      title: "Chiller",
      dataIndex: "chillerConfidence",
      key: "chiller",
      width: 150,
      render: (_value, record) => {
        // Not scored yet (gateway off/unreachable, or predates chiller scoring).
        if (record.chillerDetected == null && record.chillerConfidence == null) {
          return <Text type="secondary">—</Text>;
        }
        const pct =
          record.chillerConfidence != null
            ? Math.round(record.chillerConfidence * 100)
            : null;
        // Mask completeness — the framing signals the confidence % cannot show. Compliance
        // selection gates on these (fail-closed), so a high-% frame that is cut off is exactly
        // the one that gets rejected; surface it here next to the score.
        const cut = Array.isArray(record.chillerCutOffSides) ? record.chillerCutOffSides : [];
        const fill =
          record.chillerFrameFill != null ? Math.round(record.chillerFrameFill * 100) : null;
        return (
          <Space direction="vertical" size={2} align="start">
            {record.chillerDetected ? (
              <Tag color="green">Chiller{pct != null ? ` ${pct}%` : ""}</Tag>
            ) : (
              <Tag color="default">No chiller</Tag>
            )}
            {record.chillerMaskPresent === true && (
              <Space size={2} wrap>
                {fill != null && (
                  <Text type="secondary" style={{ fontSize: 11 }}>fill {fill}%</Text>
                )}
                {cut.length > 0 ? (
                  <Tag color="error" style={{ marginInlineEnd: 0 }}>cut off: {cut.join(", ")}</Tag>
                ) : (
                  <Tag color="green" style={{ marginInlineEnd: 0 }}>in shot</Tag>
                )}
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) =>
        status ? (
          <Tag color={status === "processed" ? "blue" : "default"}>{status}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Link",
      dataIndex: "absolutePath",
      key: "link",
      width: 90,
      render: (absolutePath) =>
        absolutePath ? (
          <a href={absolutePath} target="_blank" rel="noopener noreferrer">
            Open
          </a>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  const deviceSelect = (
    <Select
      showSearch
      allowClear
      placeholder="Select a device"
      style={{ minWidth: 260 }}
      value={deviceId || undefined}
      onChange={(v) => setDeviceId(v || null)}
      options={deviceOptions}
      optionFilterProp="label"
    />
  );

  return (
    <div>
      <PageHeader
        title="Pictures"
        subtitle="Device captures, QR & chiller detection"
        icon={<PictureOutlined style={{ fontSize: 22, color: TRAFFIC.green }} />}
        extra={deviceSelect}
      />

      {deviceId ? (
        <DataTable
          key={deviceId}
          rowKey="_id"
          columns={columns}
          fetcher={(p) => listPictures(deviceId, p)}
          filters={filters}
          showSearch={false}
          defaultPageSize={20}
          scrollX={900}
          emptyText="No pictures for this device"
          toolbar={
            <Space wrap>
              <Segmented
                value={qr}
                onChange={setQr}
                options={[
                  { label: "All", value: "all" },
                  { label: "QR only", value: "qr" },
                ]}
              />
              <RangePicker value={range} onChange={setRange} allowClear />
            </Space>
          }
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select a device to view its pictures"
          style={{ marginTop: 64 }}
        />
      )}
    </div>
  );
};

export default PictureViewer;
